/**
 * The shape `Staff::StatsSerializer` renders. Every money figure is the exact
 * decimal STRING the server computed in BigDecimal; nothing here is a number the
 * browser is allowed to do arithmetic on and put back on screen.
 */

export const STATS_RANGES = ["days30", "weeks12", "weeks26", "months12", "years"] as const

export type StatsRangeKey = (typeof STATS_RANGES)[number]

/** Mirrors `Staff::StatsRange::DEFAULT_RANGE`. */
export const DEFAULT_STATS_RANGE: StatsRangeKey = "weeks12"

export type Granularity = "day" | "week" | "month"

export type StatsRangeProps = {
  key: StatsRangeKey
  granularity: Granularity
  overlaid: boolean
  empty: boolean
  closedBucketCount: number
  label: string
  firstShiftEver: boolean
}

/** One point of the trend. `open` marks the bucket in progress. */
export type StatsBucket = {
  date: string
  count: number
  subtotal: string
  open: boolean
}

/** One cell of the hour map. `weekday` is ISO: 1 is Monday. */
export type WeekdayHour = {
  weekday: number
  hour: number
  count: number
  subtotal: string
}

export type PreviousSales = {
  transactionCount: number
  shiftCount: number
  label: string
  subtotal: string
  averageTicket: string
}

export type StatsSales = {
  transactionCount: number
  shiftCount: number
  subtotal: string
  tip: string
  averageTicket: string
  /** Over the buckets that carry a close. Computed server-side, in cents. */
  averagePerBucket: string | null
  busiestHour: number | null
  growthRatio: number | null
  perShiftGrowthRatio: number | null
  /**
   * True when the two sides of the comparison bracket different numbers of
   * closed shifts. The delta then moves onto the per-shift figure and the screen
   * says so — a total-against-total percentage across unequal service counts is
   * a lie the period report already refuses.
   */
  normalizeOnAverage: boolean
  previous: PreviousSales | null
}

export type AbcClass = "a" | "b" | "c"

export type StatsMixItem = {
  locationItemId: number | null
  name: string
  quantity: number
  abcClass: AbcClass
  share: number
  net: string
  /** `null` for a dish with no recipe. Never rendered as $0.00. */
  margin: string | null
}

export type StatsMix = {
  itemCount: number
  quantity: number
  costedQuantity: number
  net: string
  margin: string | null
  foodCostRatio: number | null
  marginRatio: number | null
  items: StatsMixItem[]
}

export type StatsLeak = {
  key: string
  label: string
  count: number
  /** `null` for reopenings, which move no money by themselves. */
  amount: string | null
  /** `null` when the window sold nothing — undefined, not zero. */
  share: number | null
}

export type Stats = {
  timeZone: string
  range: StatsRangeProps
  sales: StatsSales
  mix: StatsMix
  series: StatsBucket[]
  weekdayHours: WeekdayHour[]
  leaks: StatsLeak[]
}

export type StatsRangeOption = {
  value: StatsRangeKey
  label: string
  bucket: string
}

export type StatsPageProps = {
  stats: Stats
  range: StatsRangeKey
  ranges: StatsRangeOption[]
  hasClosedShifts: boolean
}
